import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Home, MapPin, ChevronRight, Truck, ClipboardCheck, Recycle, Sparkles, HelpCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import EstimationBanner from '../../components/EstimationBanner';
import OptimizedImage from '../../components/OptimizedImage';
import SEO from '../../components/SEO';

const DebarrasMaisonAnnecy = () => {
  const quartiers = [
    {
      nom: 'La Vieille Ville',
      description: "Ruelles pavées, immeubles anciens de 3 à 5 étages souvent sans ascenseur, cages d'escalier étroites et tournantes. Nos équipes utilisent du matériel de portage adapté pour évacuer les meubles sans endommager les boiseries et les façades classées du secteur sauvegardé."
    },
    {
      nom: 'Les Marquisats',
      description: "Quartier résidentiel en bord de lac avec des maisons individuelles et petits collectifs construits entre les années 60 et 80. Les caves et garages y accumulent souvent des décennies d'objets que les propriétaires souhaitent trier avant une revente."
    },
    {
      nom: 'Novel et Les Teppes',
      description: "Grands ensembles collectifs des années 70-80 avec parkings souterrains et caves communes. Les interventions nécessitent une coordination avec le syndic pour réserver les ascenseurs et protéger les parties communes lors des évacuations."
    },
    {
      nom: 'Bonlieu et La Gare',
      description: "Centre-ville moderne autour du centre culturel Bonlieu et de la gare SNCF. On y trouve des copropriétés des années 70-90 et des résidences récentes. Le stationnement très limité impose de planifier les créneaux de chargement avec soin."
    },
    {
      nom: 'Les Romains et Le Parmelan',
      description: "Quartiers résidentiels sur les hauteurs d'Annecy, avec des maisons individuelles dotées de jardins et de beaux volumes de stockage. Les débarras concernent souvent des propriétés familiales occupées depuis plusieurs décennies, avec caves et greniers bien remplis."
    },
    {
      nom: 'La Prairie et Courier',
      description: "Secteur calme entre le centre-ville et les rives du lac, mêlant petites maisons de ville et immeubles bourgeois. Les successions y sont fréquentes, avec des intérieurs parfois intacts depuis des années qu'il faut vider méthodiquement."
    }
  ];

  const etapes = [
    { icon: Phone, step: '1', title: 'Prise de contact', desc: 'Vous nous appelez au 04 51 40 16 17 ou remplissez notre formulaire en ligne. Nous échangeons sur votre situation et vos délais.' },
    { icon: ClipboardCheck, step: '2', title: 'Visite et devis gratuit', desc: 'Un technicien se déplace gratuitement chez vous à Annecy pour évaluer le volume, les contraintes d\'accès et vous remettre un devis ferme.' },
    { icon: Truck, step: '3', title: 'Intervention sous 48-72h', desc: 'Notre équipe débarrasse intégralement votre maison. Chaque objet est trié sur place : réemploi, don aux associations locales, recyclage.' },
    { icon: Recycle, step: '4', title: 'Tri et recyclage', desc: 'Les meubles réutilisables sont orientés vers les ressourceries d\'Annecy. Le reste est acheminé vers les filières de recyclage agréées de Haute-Savoie.' },
    { icon: Sparkles, step: '5', title: 'Remise en état', desc: 'Après l\'évacuation, nous effectuons un balayage complet. Votre maison est prête pour la suite : vente, travaux ou nouveau locataire.' }
  ];

  const avantages = [
    {
      title: 'Implantés dans le bassin annécien',
      description: 'Notre équipe connaît chaque quartier d\'Annecy, ses contraintes de stationnement, ses immeubles anciens et ses accès parfois compliqués. Pas de surprise le jour J.'
    },
    {
      title: 'Intervention rapide sous 48-72h',
      description: 'Basés à proximité, nous sommes sur place en moins de 30 minutes. Les devis sont remis sous 24h pour les situations urgentes (vente, succession, fin de bail).'
    },
    {
      title: 'Recyclage local en Haute-Savoie',
      description: 'Nous travaillons avec les ressourceries et déchèteries du bassin annécien. Plus de 70 % des objets collectés sont réemployés ou recyclés, loin des décharges.'
    },
    {
      title: 'Devis transparent, prix ferme',
      description: 'Le montant annoncé lors de la visite est celui que vous payez. Pas de supplément caché, même si le volume réel dépasse légèrement l\'estimation initiale.'
    }
  ];

  const faq = [
    {
      question: 'Intervenez-vous dans les immeubles sans ascenseur de la Vieille Ville d\'Annecy ?',
      answer: 'Oui, c\'est même l\'une de nos spécialités. Nous disposons de matériel de portage adapté (sangles, diables spéciaux, couvertures de protection) pour les escaliers étroits et les façades anciennes. Le surcoût éventuel lié aux étages est toujours inclus dans le devis initial.'
    },
    {
      question: 'Combien coûte un débarras de maison à Annecy ?',
      answer: 'Le prix dépend du volume (nombre de pièces, encombrement), de l\'accessibilité du bien et du type d\'objets à évacuer. Pour une maison de 3-4 pièces à Annecy, comptez en moyenne entre 800 et 2 500 euros. Le devis est gratuit et sans engagement.'
    },
    {
      question: 'Que deviennent les objets récupérés lors du débarras ?',
      answer: 'Tout ce qui est en bon état est orienté vers le réemploi : associations locales (Emmaüs Annecy, ressourceries du bassin), brocantes solidaires. Le reste est trié par matière (bois, métal, textile) et acheminé vers les centres de recyclage de Haute-Savoie.'
    },
    {
      question: 'Pouvez-vous intervenir rapidement pour une vente immobilière à Annecy ?',
      answer: 'Le marché immobilier annécien est très dynamique et les ventes se concluent vite. Nous proposons des interventions sous 48h pour les situations urgentes. Contactez-nous dès la signature du compromis pour planifier le débarras.'
    },
    {
      question: 'Débarrassez-vous aussi les annexes (garage, cave, grenier) en même temps que la maison ?',
      answer: 'Absolument. Nous vidons la totalité du bien en une seule intervention : pièces de vie, cuisine, salle de bain, cave, grenier, garage, abri de jardin. Tout est inclus dans le devis.'
    },
    {
      question: 'Intervenez-vous aussi dans les communes voisines comme Cran-Gevrier, Seynod ou Annecy-le-Vieux ?',
      answer: 'Bien sûr. Cran-Gevrier, Seynod, Meythet et Annecy-le-Vieux disposent chacune de leur propre page dédiée sur notre site. Nous intervenons dans tout le bassin annécien sans distinction tarifaire.'
    }
  ];

  const communesVoisines = [
    { nom: 'Seynod', lien: null },
    { nom: 'Cran-Gevrier', lien: null },
    { nom: 'Meythet', lien: null },
    { nom: 'Annecy-le-Vieux', lien: null },
    { nom: 'Sevrier', lien: null },
    { nom: 'Saint-Jorioz', lien: null },
    { nom: 'Veyrier-du-Lac', lien: null },
    { nom: 'Menthon-Saint-Bernard', lien: null },
    { nom: 'Poisy', lien: null },
    { nom: 'Argonay', lien: null },
    { nom: 'Epagny-Metz-Tessy', lien: null },
    { nom: 'Rumilly', lien: null },
    { nom: 'Thônes', lien: null },
    { nom: 'Talloires-Montmin', lien: null }
  ];

  const jsonLdLocalBusiness = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://debeco.fr/#organization",
    "name": "DEBECO - Débarras & Recyclage",
    "description": "Service professionnel de débarras de maison à Annecy et en Haute-Savoie. Vide-maison, tri sélectif, recyclage responsable.",
    "url": "https://debeco.fr/debarras-maison-annecy",
    "telephone": "+33451401617",
    "email": "contact@debeco.fr",
    "image": "https://debeco.fr/images/annecy-maisons-vue-aerienne.jpg",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Annecy",
      "addressRegion": "Haute-Savoie",
      "postalCode": "74000",
      "addressCountry": "FR"
    },
    "areaServed": {
      "@type": "City",
      "name": "Annecy",
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": "Haute-Savoie"
      }
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Débarras de maison",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Débarras de maison à Annecy",
            "description": "Vide-maison complet à Annecy : tri, évacuation, recyclage et remise en état."
          }
        }
      ]
    },
    "priceRange": "€€"
  };

  const jsonLdBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://debeco.fr/" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://debeco.fr/services" },
      { "@type": "ListItem", "position": 3, "name": "Débarras de maison", "item": "https://debeco.fr/services/maison" },
      { "@type": "ListItem", "position": 4, "name": "Annecy", "item": "https://debeco.fr/debarras-maison-annecy" }
    ]
  };

  const jsonLdFaq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer
      }
    }))
  };

  useEffect(() => {
    const scripts = [jsonLdLocalBusiness, jsonLdBreadcrumb, jsonLdFaq].map(data => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
      return script;
    });
    return () => scripts.forEach(s => s.remove());
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        title="Débarras de maison à Annecy (74) | DEBECO"
        description="Débarras de maison à Annecy : intervention sous 48-72h, devis gratuit, tri et recyclage responsable. Appelez DEBECO au 04 51 40 16 17."
        canonical="/debarras-maison-annecy"
        ogImage="/images/annecy-maisons-vue-aerienne.jpg"
      />

      {/* Breadcrumb */}
      <nav className="bg-gray-50 border-b" aria-label="Fil d'Ariane" data-testid="breadcrumb">
        <div className="container mx-auto px-4 py-3">
          <ol className="flex items-center text-sm text-gray-600 flex-wrap gap-y-1">
            <li><Link to="/" className="hover:text-emerald-600">Accueil</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li><Link to="/services" className="hover:text-emerald-600">Services</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li><Link to="/services/maison" className="hover:text-emerald-600">Débarras de maison</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li className="text-blue-900 font-medium">Annecy</li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="local-h1">Débarras de maison à Annecy</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
            Votre vide-maison professionnel dans tous les quartiers d'Annecy et du bassin annécien. Intervention rapide, devis gratuit, recyclage responsable.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-xl text-lg px-8 py-6 w-full sm:w-auto" data-testid="hero-cta-devis">
                Demander un devis gratuit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto" data-testid="hero-cta-phone">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Section: Notre service */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage
                  src="/images/annecy-maisons-vue-aerienne.jpg"
                  webpSrc="/images/annecy-maisons-vue-aerienne.webp"
                  alt="Vue aérienne d'Annecy avec ses toits et le lac turquoise, zone d'intervention DEBECO pour le débarras de maison"
                  className="w-full h-full object-cover"
                  width="1200"
                  height="801"
                  loading="eager"
                  data-testid="local-maison-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-lg">
                    <Home className="w-10 h-10 text-blue-900" />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-3xl font-bold text-blue-900 mb-6">Notre service de débarras de maison à Annecy</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Annecy est une ville où le marché immobilier reste sous tension permanente. Entre les mutations professionnelles liées à la proximité de Genève, les ventes rapides dans les quartiers prisés du bord de lac et les successions dans les maisons de la Prairie ou du Parmelan, les besoins de vide-maison sont constants.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  DEBECO intervient dans toutes ces situations avec une méthode rodée. Nous vidons intégralement votre maison — des combles au sous-sol — en triant chaque objet sur place. Ce qui peut être donné part vers les associations locales, ce qui est recyclable rejoint les filières de Haute-Savoie, et le reste est évacué dans les règles.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  Contrairement aux débarrasseurs occasionnels, nous connaissons les contraintes spécifiques des logements annéciens : immeubles anciens de la Vieille Ville sans ascenseur, stationnement limité autour de Bonlieu, accès étroits des maisons des Romains ou de Courier. Chaque intervention est préparée en amont lors de la visite gratuite.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact">
                    <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg w-full sm:w-auto">
                      Demander un devis gratuit
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:0451401617">
                    <Button variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white w-full sm:w-auto">
                      <Phone className="mr-2 w-4 h-4" />
                      04 51 40 16 17
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Quartiers */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-900 mb-4 text-center">Les quartiers d'Annecy où nous intervenons</h2>
            <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
              Chaque quartier d'Annecy a ses particularités. Notre connaissance du terrain nous permet d'adapter chaque intervention.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quartiers.map((q, index) => (
                <Card key={index} className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <h3 className="font-bold text-lg text-blue-900">{q.nom}</h3>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">{q.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Estimation Banner */}
      <EstimationBanner />

      {/* Section: Comment ça se passe */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-900 mb-10 text-center">Comment se passe un débarras de maison à Annecy ?</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {etapes.map((e) => (
                <div key={e.step} className="text-center">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <e.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">Étape {e.step}</div>
                  <h3 className="font-bold text-blue-900 mb-2">{e.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{e.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section: Pourquoi choisir DEBECO */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-900 mb-10 text-center">Pourquoi choisir DEBECO à Annecy ?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {avantages.map((a, index) => (
                <div key={index} className="flex items-start gap-4 p-6 bg-white rounded-xl shadow-sm border-2 border-transparent hover:border-emerald-500 transition-all">
                  <CheckCircle className="w-7 h-7 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-lg text-blue-900 mb-2">{a.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{a.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section: Image Annecy */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative h-80 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage
                  src="/images/annecy.jpg"
                  webpSrc="/images/annecy.webp"
                  alt="Vue panoramique du lac d'Annecy et des montagnes environnantes, zone d'intervention DEBECO pour le débarras de maison"
                  className="w-full h-full object-cover"
                  width="1200"
                  height="800"
                  loading="lazy"
                  data-testid="local-annecy-image"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-blue-900 mb-6">Zones desservies autour d'Annecy</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Au-delà d'Annecy, DEBECO intervient dans toutes les communes du bassin annécien et du pourtour du lac. Où que vous soyez, notre équipe est à moins de 30 minutes de chez vous.
                </p>
                <div className="flex flex-wrap gap-2">
                  {communesVoisines.map((c, index) => (
                    <span key={index} className="bg-blue-100 text-blue-900 px-3 py-1.5 rounded-full text-sm font-medium">
                      {c.nom}
                    </span>
                  ))}
                </div>
                <div className="mt-6">
                  <Link to="/zones/annecy" className="text-emerald-600 hover:text-emerald-700 font-medium inline-flex items-center">
                    Voir toute notre zone d'intervention Annecy
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-10">
              <HelpCircle className="w-8 h-8 text-emerald-600" />
              <h2 className="text-3xl font-bold text-blue-900">Questions fréquentes</h2>
            </div>
            <div className="space-y-4">
              {faq.map((item, index) => (
                <Card key={index} className="border-2 hover:border-emerald-500 transition-all">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg text-blue-900 mb-3">{item.question}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA finale */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Besoin d'un débarras de maison à Annecy ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contactez DEBECO pour un devis gratuit. Intervention rapide dans tous les quartiers d'Annecy et du bassin annécien.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 w-full sm:w-auto" data-testid="cta-devis-final">
                Demander un devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto" data-testid="cta-phone-final">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DebarrasMaisonAnnecy;
